export type PageviewEvent = {
  event: 'pageview'
  page: string
  page_path: string
  page_location: string
  page_title: string
  page_referrer?: string
}

/**
 * Only campaign-attribution parameters belong in analytics URLs. Application
 * state can contain raw email addresses, signatures, OAuth state, Stripe
 * Session IDs, and other secrets that must never leave Brew through GA4.
 */
const ANALYTICS_QUERY_ALLOWLIST = new Set([
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'gad_source',
  'gad_campaignid',
])

const SENSITIVE_PATH_REPLACEMENTS: ReadonlyArray<{
  pattern: RegExp
  replacement: string
}> = [
  {
    pattern: /^\/settings\/domains\/share\/[^/]+/,
    replacement: '/settings/domains/share/:token',
  },
]

function sanitizeAnalyticsPathname(pathname: string): string {
  for (const { pattern, replacement } of SENSITIVE_PATH_REPLACEMENTS) {
    if (pattern.test(pathname)) {
      return pathname.replace(pattern, replacement)
    }
  }
  return pathname
}

function sanitizeAnalyticsSearch(search: string): URLSearchParams {
  const sanitized = new URLSearchParams()
  for (const [key, value] of new URLSearchParams(search)) {
    if (ANALYTICS_QUERY_ALLOWLIST.has(key)) {
      sanitized.append(key, value)
    }
  }
  return sanitized
}

function sanitizeAnalyticsUrl(value: string): string | undefined {
  try {
    const source = new URL(value)
    if (source.protocol !== 'https:' && source.protocol !== 'http:') {
      return
    }

    const sanitized = new URL(source.origin)
    sanitized.pathname = sanitizeAnalyticsPathname(source.pathname)
    sanitized.search = sanitizeAnalyticsSearch(source.search).toString()
    return sanitized.toString()
  } catch {}
}

/**
 * Sanitized page fields attached to EVERY dataLayer event (see
 * `pushToDataLayer`). GA4 tags auto-collect `page_location` from the live
 * address bar at the moment an event fires, bypassing the sanitized custom
 * pageview payload above — so an event pushed while the URL still carries
 * application state (a Stripe Checkout Session ID, OAuth state, a share
 * token) would transmit that raw URL to Google. GA4 event tags that map
 * these Data Layer Variables override the auto-collected values.
 */
export function buildAnalyticsPageFields(
  currentLocation: string
): { page_location: string; page_path: string } | undefined {
  const pageLocation = sanitizeAnalyticsUrl(currentLocation)
  if (!pageLocation) {
    return
  }
  return {
    page_location: pageLocation,
    // `new URL` cannot throw here: sanitizeAnalyticsUrl already parsed it.
    page_path: sanitizeAnalyticsPathname(new URL(currentLocation).pathname),
  }
}

export function buildPageviewEvent(input: {
  pathname: string
  search: string
  currentLocation: string
  documentTitle: string
  documentReferrer: string
  previousLocation: string | null
}): PageviewEvent {
  const pathname = sanitizeAnalyticsPathname(input.pathname)
  const safeSearch = sanitizeAnalyticsSearch(input.search).toString()
  const page = safeSearch ? `${pathname}?${safeSearch}` : pathname
  const pageLocation = sanitizeAnalyticsUrl(input.currentLocation) ?? pathname
  const rawReferrer = input.previousLocation || input.documentReferrer
  const pageReferrer = rawReferrer
    ? sanitizeAnalyticsUrl(rawReferrer)
    : undefined

  return {
    event: 'pageview',
    page,
    page_path: pathname,
    page_location: pageLocation,
    page_title: input.documentTitle,
    ...(pageReferrer ? { page_referrer: pageReferrer } : {}),
  }
}
